import { setSchedule, flush, track, queue } from '../utils/queue';
import { isArray } from '../utils/isArray';
import { isBoolean } from '../utils/isBoolean';
import { isNumber } from '../utils/isNumber';
import { isObject } from '../utils/isObject';
import { isString } from '../utils/isString';
import { log } from '../utils/log';
import { whenLoaded, onPageHideOrVisibilityChange } from '../utils/whenLoaded';
import { objectAssign } from '../utils/assign';
import { validateIdentity, areEqual } from '../utils/validateIdentity';
import { CONSENT_DECLINED, CONSENT_GRANTED, DEFAULT_PREFIX } from '../utils/constants';
import { validateEventTypeId, validateProperties } from '../utils/validateEvent';
import { handleCustomErrors } from '../metrics/errors';
// Browser support:
// - ES5 syntax
// - assumes availability of `window.addEventListener` and `document.location`, which are broadly supported.
// - feature detection of Promise, History and Performance APIs is done by event collector modules. These features are required for some modules to collect data, but not for the RUM Agent to work.
// In Browser, the global `window` object is polluted with the agent
export var isBrowser = typeof window === 'object' && typeof document === 'object';
export var globalRef = isBrowser ? window : {};
(function (w, isBrowser) {
    if (w.SplitRumAgent)
        return;
    var _config = {
        i: [],
        a: false,
        p: {},
        prefix: DEFAULT_PREFIX,
        url: 'https://events.split.io/api',
        pushRate: 30,
        queueSize: 5000,
        log: log,
        userConsent: CONSENT_GRANTED, // User consent is assumed to be granted by default
    };
    var _isConfigured = false;
    function calculateAndFlush() {
        if (isBrowser && !_isConfigured && _config.a && _config.i.length) {
            _isConfigured = true;
            // always async never inasync ;)
            setTimeout(flush, 0);
        }
    }
    var collectors = [];
    // SplitRumAgent would be our namespace
    var SplitRumAgent = {
        // Initialization params
        setup: function (sdkKey, config) {
            if (isObject(config)) {
                var log_1 = config.log, prefix = config.prefix, url = config.url, pushRate = config.pushRate, queueSize = config.queueSize, userConsent = config.userConsent;
                if (log_1)
                    _config.log = log_1;
                if (isString(prefix) && (prefix === '' || validateEventTypeId(prefix, _config.log, 'prefix')))
                    _config.prefix = prefix;
                if (isString(url))
                    _config.url = url;
                if (isNumber(pushRate))
                    _config.pushRate = pushRate;
                if (isNumber(queueSize))
                    _config.queueSize = queueSize;
                if (isString(userConsent))
                    _config.userConsent = userConsent;
            }
            if (isString(sdkKey)) {
                _config.a = sdkKey;
                calculateAndFlush();
            }
            else {
                _config.log.error('SDK key must be a string.');
            }
            // allow for chaining.
            return SplitRumAgent;
        },
        // Configure identities
        addIdentities: function (identities) {
            if (isArray(identities)) {
                identities.forEach(function (identity) { return SplitRumAgent.addIdentity(identity); });
            }
            else {
                _config.log.error('Identities must be an array of objects with key and trafficType.');
            }
        },
        addIdentity: function (identity) {
            var validatedIdentity = validateIdentity(identity, _config.log);
            if (validatedIdentity && !_config.i.some(function (i) { return areEqual(i, validatedIdentity); })) {
                _config.i.push(validatedIdentity);
                calculateAndFlush();
            }
        },
        removeIdentities: function () {
            _config.i = [];
        },
        removeIdentity: function (identity) {
            var validatedIdentity = validateIdentity(identity, _config.log);
            if (validatedIdentity) {
                _config.i = _config.i.filter(function (i) { return !areEqual(i, validatedIdentity); });
            }
        },
        getIdentities: function () {
            return _config.i;
        },
        // Custom error handler.
        trackError: function (e) {
            return handleCustomErrors(e, SplitRumAgent);
        },
        // Track custom events.
        track: track,
        // Register event collector
        register: function (eventCollector) {
            if (typeof eventCollector !== 'function') {
                _config.log.error('register method must be called with an event collector function.');
            }
            else {
                collectors.push(eventCollector(SplitRumAgent));
            }
            // allow for chaining.
            return SplitRumAgent;
        },
        // Custom properties
        setProperties: function (properties) {
            objectAssign(_config.p, validateProperties(properties, _config.log));
        },
        getProperties: function () {
            return _config.p;
        },
        removeProperties: function () {
            _config.p = {};
        },
        // User consent
        setUserConsent: function (userConsent) {
            if (!isBoolean(userConsent)) {
                _config.log.warn('setUserConsent: provided param must be a boolean value');
                return false;
            }
            _config.userConsent = userConsent ? CONSENT_GRANTED : CONSENT_DECLINED;
            return true;
        },
        getUserConsent: function () {
            return _config.userConsent;
        },
        // Flush collectors and send events
        flush: function (useBeacon) {
            collectors.forEach(function (collector) {
                if (collector && typeof collector.flush === 'function')
                    collector.flush();
            });
            return flush(useBeacon);
        },
        // Private members for internal use only.
        // @TODO review how to expose config
        __getConfig: function () {
            return _config;
        },
        __queue: queue,
        __collectors: collectors,
    };
    if (isBrowser) {
        // Post events periodically
        whenLoaded(setSchedule);
        // If there's a pagehide or visibilitychange event, calculate what's possible and send events using beacon
        onPageHideOrVisibilityChange(function () {
            SplitRumAgent.flush(true);
        });
    }
    w.SplitRumAgent = SplitRumAgent;
})(globalRef, isBrowser);
export var SplitRumAgent = globalRef.SplitRumAgent;
